/**
 * External
 */
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  useLocation, useParams,
} from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import isEmptyL from 'lodash-es/isEmpty';
import lastL from 'lodash-es/last';
import getL from 'lodash-es/get';

/* Ott-common */
import { gtmEvent } from '@dtvgo/gtm-event-report';

/* Assets */
import logo from '@/assets/theme/svg/logo.svg';

/**
 * Actions
 */
import classNames from 'classnames';
import { setShowSearch as setShowSearchAction } from '../state/search/searchSlice';

/* Components */
import { gtmDimensions } from '../components/GtmContext/dimensions/pdp';
import { useGtmContext } from '../components/GtmContext';

/* Other */
import { getImage } from '../theme/images';
import { useIsResponsive } from '../hooks/useIsResponsive';
import {
  alcatrazFeatureFlag, decryptAllData, formatContentForGtm,
  getSelfCareLinkAction, getSelfCareURL,
  isBrazil,
  PUBLIC_PATHS,
  redirectToPlansPage,
} from '../utils';

/**
 * Styles
*/
import './NavBarPdp.scss';

function NavBarPdp(props) {
  const {
    user,
    barNotification,
    forwardedRef,
    userCountry,
  } = props;
  const isMobile = useIsResponsive('(max-width: 1024px)');
  const location = useLocation();
  const { country } = useParams() || {};
  const storeCode = (userCountry || country || '').toLowerCase();
  const [topMenuByBarHeight, setTopMenuByBarHeight] = useState('0');
  const [isScrolling, setIsScrolling] = useState(false);
  const {
    gtmUserData,
    gtmCollectionName,
    gtmProviderName,
    setGtmCollectionName,
    setGtmProviderName,
  } = useGtmContext();
  const { account } = user;

  const selfCareUrl = getSelfCareURL(user);
  const selfCareLinkAction = getSelfCareLinkAction(props);

  function selfCarePage() {
    if (alcatrazFeatureFlag) {
      decryptAllData();
    }
    selfCareLinkAction();
  }

  const handleClick = (caption) => {
    if (caption === 'plans') {
      redirectToPlansPage(storeCode);
      return;
    }

    const { hash, eventType, dimensions } = gtmDimensions[caption].watch;

    const dataTosend = formatContentForGtm(
      {},
      {
        tab: location.state?.tab || null,
        providerName: gtmProviderName,
        collectionName: gtmCollectionName,
      },
    );

    gtmEvent({
      hash,
      eventType,
      dimensions,
      userData: gtmUserData,
      data: dataTosend,
    });

    setGtmCollectionName(null);
    setGtmProviderName(null);
  };

  const handleScroll = useCallback(() => {
    const pdpContainer = forwardedRef.current;

    if (pdpContainer) {
      setIsScrolling(pdpContainer.scrollTop > 0);
    }
  }, [forwardedRef]);

  useEffect(() => {
    const pdpContainer = forwardedRef.current;
    if (pdpContainer) {
      pdpContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      if (pdpContainer) {
        pdpContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [forwardedRef, handleScroll]);

  const setTopMenu = useCallback(() => {
    if (barNotification?.show) {
      setTimeout(() => {
        const barNotificationHeight = document.getElementsByClassName('dtv-bar-notification');
        if (barNotificationHeight.length > 0) {
          const height = barNotificationHeight[0].offsetHeight - 1;
          setTopMenuByBarHeight(`${height}px`);
        }
      }, 200);
    }
  }, [barNotification]);

  useEffect(() => {
    if (!barNotification?.show) {
      setTopMenuByBarHeight('0');
    } else {
      setTopMenu();
    }
  }, [barNotification, setTopMenu]);

  let userName = null;
  if (!isEmptyL(account)) {
    userName = account.userName ? account.userName.toLowerCase() : account.profile.email;
  }

  const getLinkUrl = (url) => (storeCode && !isBrazil ? `/${storeCode}${url}` : url);

  let captionLink;
  const menuLinks = [
    {
      caption: 'plans',
      default: 'Planes',
      url: getLinkUrl(PUBLIC_PATHS.PLANS),
    },
    {
      caption: 'extras',
      default: 'Extras',
      url: getLinkUrl(PUBLIC_PATHS.EXTRAS),
    },
    {
      caption: 'live',
      default: 'En vivo',
      url: getLinkUrl(PUBLIC_PATHS.LIVE),
    },
    {
      caption: 'sports',
      default: 'Deportes',
      url: getLinkUrl(PUBLIC_PATHS.SPORTS),
    },
    {
      caption: 'watch',
      default: 'Catálogo',
      url: getLinkUrl(PUBLIC_PATHS.HELP),
    },
    {
      caption: 'help',
      default: 'Ayuda',
      url: getLinkUrl(PUBLIC_PATHS.HELP),
    },
  ];

  if (isMobile) {
    return (
      <div
        className={classNames('dtv-nav-bar-pdp-container px-4', {
          'dtv-nav-bar-pdp-container__scrolling': isScrolling,
        })}
        style={{ top: topMenuByBarHeight }}
      >
        <div className="dtv-nav-bar-pdp-left">
          <img className="dtv-nav-bar-pdp-logo" src={getImage('logo') || logo} alt="Logo" />
        </div>
        <div className="dtv-nav-bar-pdp-right">
          {userName && (
            <a href={selfCareUrl} onClick={() => selfCarePage()} className="dtv-nav-bar-pdp-right-link">
              <FormattedMessage id="menu.myPlan" defaultMessage="Mi plan" />
            </a>
          )}
          {!userName && (
            <a href={PUBLIC_PATHS.HELP} className="dtv-nav-bar-pdp-right-link">
              <FormattedMessage id="menu.help" defaultMessage="Ayuda" />
            </a>
          )}
        </div>
      </div>
    );
  }

  if (!isMobile && !userName) {
    return (
      <div
        className={classNames('dtv-nav-bar-pdp-container', {
          'dtv-public-menu': !userName,
          'dtv-nav-bar-pdp-container__scrolling': isScrolling,
        })}
        style={{ top: topMenuByBarHeight }}
      >
        <div className="dtv-nav-bar-pdp-content">
          <div className="dtv-nav-bar-pdp-content-left">
            <a href="/home" className="dtv-logo">
              <img src={getImage('logo') || logo} alt="Logo" />
            </a>
            <div className="dtv-navlinks">
              {menuLinks.map((menuLink) => {
                captionLink = <FormattedMessage id={`menu.${menuLink.caption}`} defaultMessage={menuLink.default} />;
                return (
                  <a
                    href={menuLink.url}
                    key={menuLink.caption}
                    className="dtv-navlink-item"
                    onClick={() => handleClick(menuLink.caption)}
                  >
                    {captionLink}
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

function mapStateToProps({
  user, notifications, router, global,
}) {
  const { userCountry } = getL(global, 'configurationAEM.data', '');
  const activeOption = lastL(getL(router, 'location.pathname').split('/'));
  const barNotification = notifications.get('barNotification');
  const notificationHeight = notifications.get('notificationHeight');
  const navBarHeight = notifications.get('navBarHeight');
  return {
    user,
    barNotification,
    notificationHeight,
    navBarHeight,
    activeOption,
    userCountry,
  };
}

export default connect(mapStateToProps, {
  setShowSearch: setShowSearchAction,
})(injectIntl(NavBarPdp));
